/**
 * Version: V0.0.4.2
 * Created: 2026-06-12
 * Updated: 2026-06-13
 * Description: Main App entry component handling splash load state, subdomain-based dashboard routing, and public layouts.
 * Added legal page routes mapping to Privacy, Terms, and Cookies pages.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { LanguageProvider } from './context/LanguageContext';

// Style Tokens
import './styles/variables.css';
import './index.css';

// Layouts & Global Systems
import MainLayout from './layouts/MainLayout';
import PageLoader from './components/common/PageLoader';
import Prefetcher from './components/Prefetcher';
import SplashPage from './pages/SplashPage';
import {
  bootstrapSession,
  clearInMemorySession,
  clearLegacySessionCookie,
} from './services/apiClient';
import { evaluatePortalAccess } from './services/portalAccess';

const DevRoleSwitcherBarLazy = React.lazy(() => import('./components/common/DevRoleSwitcherBar'));
const RouteHelmetProvider = React.lazy(() => import('./components/common/RouteHelmetProvider'));
const DevRoleSwitcherBar = (props) => (
  <React.Suspense fallback={null}>
    <DevRoleSwitcherBarLazy {...props} />
  </React.Suspense>
);

// Public Pages
const HomePage = React.lazy(() => import('./pages/HomePage'));
const FeaturesPage = React.lazy(() => import('./pages/FeaturesPage'));
const SupportPage = React.lazy(() => import('./pages/support/SupportPage'));
const DocsDetailPage = React.lazy(() => import('./pages/support/DocsDetailPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const DemoRequestPage = React.lazy(() => import('./pages/DemoRequestPage'));
const LegalPage = React.lazy(() => import('./pages/LegalPage'));
const AboutUsPage = React.lazy(() => import('./pages/AboutUsPage'));
const BlogPage = React.lazy(() => import('./pages/BlogPage'));
const BlogPostDetailPage = React.lazy(() => import('./pages/BlogPostDetailPage'));
const TestimonialsPage = React.lazy(() => import('./pages/TestimonialsPage'));
const SitemapPage = React.lazy(() => import('./pages/SitemapPage'));
const StatusPage = React.lazy(() => import('./pages/StatusPage'));
const CareersPage = React.lazy(() => import('./pages/CareersPage'));
const PricingPage = React.lazy(() => import('./pages/PricingPage'));
const PartnersPage = React.lazy(() => import('./pages/PartnersPage'));
const DownloadsPage = React.lazy(() => import('./pages/DownloadsPage'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const SignupPage = React.lazy(() => import('./pages/SignupPage'));
const ActivatePage = React.lazy(() => import('./pages/ActivatePage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const OpenTicketPage = React.lazy(() => import('./pages/support/OpenTicketPage'));
const TrackTicketPage = React.lazy(() => import('./pages/support/TrackTicketPage'));


// Portal Pages
const SupportDashboardPage = React.lazy(() => import('./pages/support/SupportDashboardPage'));
const PatientDashboardPage = React.lazy(() => import('./pages/patient/PatientDashboardPage'));
const ClinicDashboardPage = React.lazy(() => import('./pages/clinic/ClinicDashboardPage'));
const LabDashboardPage = React.lazy(() => import('./pages/lab/LabDashboardPage'));
const SupplierDashboardPage = React.lazy(() => import('./pages/supplier/SupplierDashboardPage'));
const WasteDashboardPage = React.lazy(() => import('./pages/waste/WasteDashboardPage'));
const RepairDashboardPage = React.lazy(() => import('./pages/repair/RepairDashboardPage'));
const TrackingDashboardPage = React.lazy(() => import('./pages/tracking/TrackingDashboardPage'));



function AppContent() {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [authResolved, setAuthResolved] = useState(false);
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('dentiefy-theme') || 'light';
  });
  useEffect(() => {
    let active = true;
    const resolveSession = async () => {
      try {
        const resolved = await bootstrapSession();
        if (!active) return;
        setSession(resolved);
        if (!resolved) {
          const devDom = process.env.REACT_APP_MAIN_DOMAIN_DEV || 'dentiefy.test';
          const isLocal = window.location.hostname.includes('localhost') ||
                          window.location.hostname.includes('127.0.0.1') ||
                          window.location.hostname.includes(devDom);
          if (isLocal) localStorage.setItem('dentiefy-dev-role', 'VISITOR');
        }
      } catch (error) {
        if (active) setSession(null);
      } finally {
        if (active) setAuthResolved(true);
      }
    };
    resolveSession();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const handleAuthenticationRequired = () => {
      setSession(null);
    };
    window.addEventListener('dentiefy-auth-required', handleAuthenticationRequired);
    return () => window.removeEventListener('dentiefy-auth-required', handleAuthenticationRequired);
  }, []);

  const handleLogout = useCallback(async ({ serverSessionEnded = false } = {}) => {
    const logoutHostname = window.location.hostname.toLowerCase();
    const isSupportPortalHost = logoutHostname.startsWith('supports.');
    const canUseSupportPortal = session?.capabilities?.includes('support:portal:access') === true;
    if (!serverSessionEnded && !isSupportPortalHost && canUseSupportPortal) {
      const baseHost = window.location.host.replace(/^(supports\.|patients\.|clinics\.|labs\.|suppliers\.|waste-management\.|repair-service\.|tracking\.)/, '');
      window.location.href = `${window.location.protocol}//supports.${baseHost}/?logout=1`;
      return;
    }
    if (!serverSessionEnded) {
      try {
        const { logoutUser } = await import('./services/auth');
        await logoutUser();
      } catch (error) {
        console.warn('Server logout was not confirmed; the authenticated view remains active.', error.code || error);
        return false;
      }
    } else {
      clearInMemorySession();
    }

    // Reset developer simulation bar role to VISITOR
    localStorage.setItem('dentiefy-dev-role', 'VISITOR');
    clearLegacySessionCookie();
    setSession(null);

    // Redirect to main domain's login page with logout query parameter
    const protocol = window.location.protocol;
    const host = window.location.host;
    const cleanedHost = host.replace(/^(supports\.|patients\.|clinics\.|labs\.|suppliers\.|waste-management\.|repair-service\.|tracking\.)/, '');
    window.location.href = `${protocol}//${cleanedHost}/login?logout=success`;
    return true;
  }, [session]);

  const hostname = window.location.hostname.toLowerCase();
  const isSupportPortal = hostname.startsWith('supports.');
  const isPatientPortal = hostname.startsWith('patients.');
  const isClinicPortal = hostname.startsWith('clinics.');
  const isLabPortal = hostname.startsWith('labs.');
  const isSupplierPortal = hostname.startsWith('suppliers.');
  const isWastePortal = hostname.startsWith('waste-management.');
  const isRepairPortal = hostname.startsWith('repair-service.');
  const isTrackingPortal = hostname.startsWith('tracking.');
  const isStatusPortal = hostname.startsWith('status.');

  const ROLE_SUBDOMAIN_MAP = {
    // ── Global Admin (Support Portal) ────────────────────────────────────────
    OWNER:                'supports',
    MODERATOR:            'supports',
    ENGINEER:             'supports',
    BILLING_ADMIN:        'supports',
    SECURITY_ADMIN:       'supports',
    SALES_MGR:            'supports',
    SUPPORT_AGENT:        'supports',
    SUPPORT_MGR:          'supports',
    COMPLIANCE_MOD:       'supports',
    // ── Patients & Family Portal ─────────────────────────────────────────────
    PATIENT:              'patients',
    PATIENT_PRIMARY:      'patients',
    PATIENT_COMANAGER:    'patients',
    PATIENT_ADOLESCENT:   'patients',
    // ── Dental Clinic Portal ─────────────────────────────────────────────────
    CLINIC:               'clinics',
    CLINIC_OWNER:         'clinics',
    CLINIC_MANAGER:       'clinics',
    CLINIC_DENTIST:       'clinics',
    CLINIC_ASSISTANT:     'clinics',
    CLINIC_FRONT_DESK:    'clinics',
    // ── Dental Lab Portal ────────────────────────────────────────────────────
    LAB:                  'labs',
    LAB_OWNER:            'labs',
    LAB_MANAGER:          'labs',
    LAB_SENIOR_TECH:      'labs',
    LAB_JUNIOR_TECH:      'labs',
    LAB_DISPATCH:         'labs',
    // ── Dental Supplier Portal ───────────────────────────────────────────────
    SUPPLIER:             'suppliers',
    SUPPLIER_OWNER:       'suppliers',
    SUPPLIER_CATALOG:     'suppliers',
    SUPPLIER_SALES:       'suppliers',
    SUPPLIER_BILLING:     'suppliers',
    SUPPLIER_WAREHOUSE:   'suppliers',
    // ── Waste Management Portal ──────────────────────────────────────────────
    WASTE_MANAGEMENT:     'waste-management',
    WASTE_OWNER:          'waste-management',
    WASTE_DISPATCH:       'waste-management',
    WASTE_COMPLIANCE:     'waste-management',
    WASTE_DRIVER:         'waste-management',
    // ── Equipment Maintenance & Repair Portal ────────────────────────────────
    REPAIR_SERVICE:       'repair-service',
    REPAIR_OWNER:         'repair-service',
    REPAIR_DISPATCH:      'repair-service',
    REPAIR_TECHNICIAN:    'repair-service',
    REPAIR_PARTS:         'repair-service',
    REPAIR_BILLING:       'repair-service',
    // ── Logistics & Delivery (Tracking) Portal ───────────────────────────────
    TRACKING:             'tracking',
    LOGISTICS_OWNER:      'tracking',
    LOGISTICS_DISPATCH:   'tracking',
    LOGISTICS_COURIER:    'tracking',
    LOGISTICS_SUPPORT:    'tracking',
    LOGISTICS_BILLING:    'tracking',
  };

  const devDom = process.env.REACT_APP_MAIN_DOMAIN_DEV || 'dentiefy.test';
  const isDev = process.env.NODE_ENV === 'development' || 
                hostname.includes('localhost') || 
                hostname.includes('127.0.0.1') || 
                hostname.includes(devDom);

  const protocol = window.location.protocol;
  const host = window.location.host;
  const cleanedHost = host.replace(/^(supports\.|patients\.|clinics\.|labs\.|suppliers\.|waste-management\.|repair-service\.|tracking\.)/, '');

  // 1. Render splash screen during initial load
  if (loading || !authResolved) {
    return <SplashPage onFinished={() => setLoading(false)} />;
  }

  const redirectToLogin = () => {
    window.location.href = `${protocol}//${cleanedHost}/login`;
    return null;
  };

  const redirectToCorrectPortal = (sub) => {
    window.location.href = `${protocol}//${sub}.${cleanedHost}/`;
    return null;
  };

  const DashboardRedirect = () => {
    if (!session) {
      return <Navigate to="/login" replace />;
    }
    const correctSub = session.user.subdomain || ROLE_SUBDOMAIN_MAP[session.user.role] || 'patients';
    window.location.href = `${protocol}//${correctSub}.${cleanedHost}/`;
    return null;
  };

  // 2. Subdomain Portal Routing
  if (isSupportPortal) {
    if (!session) return redirectToLogin();
    const access = evaluatePortalAccess('supports', session);
    if (!access.allowed) {
      if (!access.redirectPortal) {
        window.location.href = `${protocol}//${cleanedHost}/`;
        return null;
      }
      return redirectToCorrectPortal(access.redirectPortal);
    }
    return (
      <>
        {isDev && <DevRoleSwitcherBar session={session} setSession={setSession} theme={theme} />}
        <SupportDashboardPage session={session} setSession={setSession} theme={theme} setTheme={setTheme} onLogout={handleLogout} />
      </>
    );
  }

  if (isPatientPortal) {
    if (!session) return redirectToLogin();
    const roleSub = session.user.subdomain || ROLE_SUBDOMAIN_MAP[session.user.role];
    if (roleSub !== 'patients') {
      return redirectToCorrectPortal(roleSub || 'patients');
    }
    return (
      <>
        {isDev && <DevRoleSwitcherBar session={session} setSession={setSession} theme={theme} />}
        <PatientDashboardPage session={session} setSession={setSession} theme={theme} setTheme={setTheme} onLogout={handleLogout} />
      </>
    );
  }

  if (isClinicPortal) {
    if (!session) return redirectToLogin();
    const roleSub = session.user.subdomain || ROLE_SUBDOMAIN_MAP[session.user.role];
    if (roleSub !== 'clinics') {
      return redirectToCorrectPortal(roleSub || 'patients');
    }
    return (
      <>
        {isDev && <DevRoleSwitcherBar session={session} setSession={setSession} theme={theme} />}
        <ClinicDashboardPage session={session} setSession={setSession} theme={theme} setTheme={setTheme} onLogout={handleLogout} />
      </>
    );
  }

  if (isLabPortal) {
    if (!session) return redirectToLogin();
    const roleSub = session.user.subdomain || ROLE_SUBDOMAIN_MAP[session.user.role];
    if (roleSub !== 'labs') {
      return redirectToCorrectPortal(roleSub || 'patients');
    }
    return (
      <>
        {isDev && <DevRoleSwitcherBar session={session} setSession={setSession} theme={theme} />}
        <LabDashboardPage session={session} setSession={setSession} theme={theme} setTheme={setTheme} onLogout={handleLogout} />
      </>
    );
  }

  if (isSupplierPortal) {
    if (!session) return redirectToLogin();
    const roleSub = session.user.subdomain || ROLE_SUBDOMAIN_MAP[session.user.role];
    if (roleSub !== 'suppliers') {
      return redirectToCorrectPortal(roleSub || 'patients');
    }
    return (
      <>
        {isDev && <DevRoleSwitcherBar session={session} setSession={setSession} theme={theme} />}
        <SupplierDashboardPage session={session} setSession={setSession} theme={theme} setTheme={setTheme} onLogout={handleLogout} />
      </>
    );
  }

  if (isWastePortal) {
    if (!session) return redirectToLogin();
    const roleSub = session.user.subdomain || ROLE_SUBDOMAIN_MAP[session.user.role];
    if (roleSub !== 'waste-management') {
      return redirectToCorrectPortal(roleSub || 'patients');
    }
    return (
      <>
        {isDev && <DevRoleSwitcherBar session={session} setSession={setSession} theme={theme} />}
        <WasteDashboardPage session={session} setSession={setSession} theme={theme} setTheme={setTheme} onLogout={handleLogout} />
      </>
    );
  }

  if (isRepairPortal) {
    if (!session) return redirectToLogin();
    const roleSub = session.user.subdomain || ROLE_SUBDOMAIN_MAP[session.user.role];
    if (roleSub !== 'repair-service') {
      return redirectToCorrectPortal(roleSub || 'patients');
    }
    return (
      <>
        {isDev && <DevRoleSwitcherBar session={session} setSession={setSession} theme={theme} />}
        <RepairDashboardPage session={session} setSession={setSession} theme={theme} setTheme={setTheme} onLogout={handleLogout} />
      </>
    );
  }

  if (isTrackingPortal) {
    if (!session) return redirectToLogin();
    const roleSub = session.user.subdomain || ROLE_SUBDOMAIN_MAP[session.user.role];
    if (roleSub !== 'tracking') {
      return redirectToCorrectPortal(roleSub || 'patients');
    }
    return (
      <>
        {isDev && <DevRoleSwitcherBar session={session} setSession={setSession} theme={theme} />}
        <TrackingDashboardPage session={session} setSession={setSession} theme={theme} setTheme={setTheme} onLogout={handleLogout} />
      </>
    );
  }

  if (isStatusPortal) {
    return <StatusPage />;
  }

  // 3. Main Domain & Testing Route Routing
  return (
    <>
      {isDev && <DevRoleSwitcherBar session={session} setSession={setSession} theme={theme} />}
      <MainLayout
        session={session}
        onLogout={handleLogout}
        theme={theme}
        setTheme={setTheme}
      >
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/features" element={<RouteHelmetProvider><FeaturesPage /></RouteHelmetProvider>} />
        <Route path="/about-us" element={<AboutUsPage />} />
        <Route path="/careers" element={<CareersPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/blog" element={<RouteHelmetProvider><BlogPage /></RouteHelmetProvider>} />
        <Route path="/blog/:slug" element={<RouteHelmetProvider><BlogPostDetailPage /></RouteHelmetProvider>} />
        <Route path="/testimonials" element={<RouteHelmetProvider><TestimonialsPage /></RouteHelmetProvider>} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/docs/:slug" element={<DocsDetailPage />} />
        <Route path="/contact" element={<ContactPage session={session} />} />
        <Route path="/demo-request" element={<DemoRequestPage />} />
        <Route path="/privacy-policy" element={<LegalPage defaultSlug="privacy-policy" />} />
        <Route path="/terms-of-service" element={<LegalPage defaultSlug="terms-of-service" />} />
        <Route path="/cookie-policy" element={<LegalPage defaultSlug="cookie-policy" />} />
        <Route path="/sitemap" element={<SitemapPage />} />
        <Route path="/status" element={<StatusPage />} />
        <Route path="/downloads" element={<DownloadsPage />} />
        <Route path="/partners" element={<PartnersPage />} />
        
        {/* Support Tickets Routes */}
        <Route path="/tickets/new" element={<OpenTicketPage />} />
        <Route path="/tickets/track/:ticketNumber" element={<TrackTicketPage />} />

        {/* Dashboard Test Route (for local testing on main domain) */}
        <Route 
          path="/dashboard" 
          element={<DashboardRedirect />} 
        />

        {/* Fallbacks */}
        <Route path="/login" element={session ? <Navigate to="/dashboard" replace /> : <LoginPage setSession={setSession} theme={theme} setTheme={setTheme} />} />
        <Route path="/signup" element={session ? <Navigate to="/dashboard" replace /> : <SignupPage setSession={setSession} theme={theme} setTheme={setTheme} />} />
        <Route path="/activate" element={session ? <Navigate to="/dashboard" replace /> : <ActivatePage setSession={setSession} theme={theme} setTheme={setTheme} />} />
        <Route path="/forgot-password" element={session ? <Navigate to="/dashboard" replace /> : <ForgotPasswordPage theme={theme} setTheme={setTheme} />} />
        <Route path="/reset-password" element={session ? <Navigate to="/dashboard" replace /> : <ResetPasswordPage theme={theme} setTheme={setTheme} />} />
        <Route path="*" element={
          <div className="text-center py-20 text-slate-500">
            <p className="text-lg font-semibold font-sans">Page Not Found</p>
            <p className="text-xs text-slate-400 font-sans mt-1">Check the URL or return home.</p>
          </div>
        } />
      </Routes>
    </MainLayout>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <Prefetcher />
        <div className="App">
          <React.Suspense fallback={<PageLoader />}><AppContent /></React.Suspense>
        </div>
      </LanguageProvider>
    </BrowserRouter>
  );
}
